import React from 'react';
import { DollarSign, Calendar, Clock, Smartphone, Layers, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета стоимости AR-проекта
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  const formatUSD = (value) => {
    return `${formatNumber(value, 0)}`;
  };
  
  // Расчет стоимости за месяц разработки
  const monthlyCost = results.totalCost / results.timelineMonths;

  return (
    <ToolResult title="Результаты расчета стоимости AR-проекта">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость проекта
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatUSD(results.totalCost)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Полная стоимость разработки AR-проекта{results.maintenanceCost > 0 ? ', включая поддержку' : ''}
          </p>
        </div>

        {/* Сроки и ресурсы */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-cyan-50 dark:from-emerald-900/30 dark:to-cyan-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Сроки и ресурсы
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {results.timelineMonths} мес.
            </span>
            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
              (~{formatNumber(results.estimatedHours)} ч.)
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Примерное время и объем работ в человеко-часах
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип AR-проекта" 
          value={results.projectType} 
          color="indigo" 
          icon={<Smartphone className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Объем проекта" 
          value={results.projectScope} 
          color="indigo" 
          icon={<Layers className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Стоимость разработки" 
          value={formatUSD(results.subtotal - (results.maintenanceCost || 0))}
          color="green" 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
        
        {results.maintenanceCost > 0 && (
          <ResultItem 
            label="Стоимость поддержки" 
            value={formatUSD(results.maintenanceCost)} 
            color="blue"
            icon={<DollarSign className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Средняя стоимость в месяц" 
          value={formatUSD(monthlyCost)} 
        />
        
        <ResultItem 
          label="Количество платформ" 
          value={results.platforms} 
        />
        
        <ResultItem 
          label="Количество функций" 
          value={results.features} 
        />
        
        <ResultItem 
          label="Примерное количество часов" 
          value={formatNumber(results.estimatedHours)} 
          color="orange" 
          icon={<Clock className="w-4 h-4"/>}
        />

        {/* Информация о времени разработки */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Важно о стоимости AR-разработки</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Оценка стоимости и сроков является приблизительной и может варьироваться в зависимости от конкретных требований и изменений в процессе разработки. 
                Рекомендуется заложить 15-20% бюджета на непредвиденные расходы.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;